'use client';

import type { FeedbackRating } from 'conversational-search-client';
import { type JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const MAX_COMMENT_LENGTH = 2000;

const COMMENT_LABELS: Record<FeedbackRating, string> = {
    up: 'What went well?',
    down: 'What could have gone better?',
};

interface FeedbackCommentBoxProps {
    onSubmit: (comment: string) => void;
    disabled: boolean;
    rating: FeedbackRating;
}

export function FeedbackCommentBox({ onSubmit, disabled, rating }: FeedbackCommentBoxProps): JSX.Element {
    const [text, setText] = useState('');
    const trimmed = text.trim();
    const canSubmit = !disabled && trimmed.length > 0;

    return (
        <div className="flex flex-col gap-2">
            <label htmlFor="feedback-comment" className="text-xs font-semibold leading-normal text-[#1F2830]">
                {COMMENT_LABELS[rating]}
            </label>
            <textarea
                id="feedback-comment"
                aria-label="Feedback comment"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={MAX_COMMENT_LENGTH}
                rows={2}
                placeholder="Write a comment..."
                disabled={disabled}
                className="w-full resize-none rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm outline-none placeholder:text-gray-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
            <button
                type="button"
                disabled={!canSubmit}
                onClick={() => onSubmit(trimmed)}
                className={twMerge(
                    'self-stretch rounded-md px-4 py-2 text-sm font-medium transition-colors',
                    canSubmit
                        ? 'cursor-pointer bg-gray-800 text-white hover:bg-gray-700'
                        : 'cursor-not-allowed bg-gray-300 text-gray-500',
                )}
            >
                Submit feedback
            </button>
        </div>
    );
}
